import i18n from "src/i18n";
import {
  AccidentCircumstanceKeyEnum,
  InjuredType,
  VehicleOwnerType,
  VehicleType,
  YesNoAnswer,
} from "src/types";
import * as yup from "yup";
import { regexDate, regexHour, regexTaxCode, regexTaxCodeOrVat } from "../regex";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { CountryData } from "react-phone-input-2";

export const ystr = yup.string();
export const ystrRequired = ystr.required(i18n.t("error.required"));
export const ystrRequiredYesNo = ystrRequired.oneOf(
  Object.values(YesNoAnswer),
  i18n.t("error.invalid_value"),
);
export const ystrRequiredOwnerType = ystrRequired.oneOf(
  Object.values(VehicleOwnerType),
  i18n.t("error.invalid_value"),
);
export const ystrRequiredInjuredType = ystrRequired.oneOf(
  Object.values(InjuredType),
  i18n.t("error.invalid_value"),
);
export const ystrRequiredVehicleType = ystrRequired.oneOf(
  Object.values(VehicleType),
  i18n.t("error.invalid_value"),
);
export const ystrAccidentCircumstance = ystr.oneOf(
  Object.values(AccidentCircumstanceKeyEnum),
  i18n.t("error.invalid_value"),
);
export const yupDate = ystr.matches(regexDate, i18n.t("error.invalid_date"));
export const yupHour = ystr.matches(regexHour, i18n.t("error.invalid_hour"));
export const yupDateRequired = yupDate.required(i18n.t("error.required"));
export const yupEmail = yup.string().email(i18n.t("error.invalid_email"));
export const yupEmailRequired = yupEmail.required(i18n.t("error.required"));
export const yupBool = yup.bool();
export const yupTaxCode = ystr.matches(
  regexTaxCode,
  i18n.t("error.invalid_tax_code"),
);
export const yupTaxCodeOrVat = ystr.matches(
  regexTaxCodeOrVat,
  i18n.t("error.invalid_value"),
);

export const usePhoneNumberSchema = ({
  isRequired = false,
}: {
  isRequired: boolean;
}) => {
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    formattedPhone: ystr
      .test(
        "validate-insured-phone-required",
        t("error.required"),
        function () {
          if (!isRequired) return true;

          const { country, value } = this.parent;
          const formattedValue = value?.replace(/\D/g, "");
          const dialCode = (country as CountryData)?.dialCode;

          if (!formattedValue || formattedValue === dialCode) {
            return false;
          }

          return true;
        },
      )
      .test("validate-insured-phone", t("error.invalid_value"), function () {
        const { country, value } = this.parent;
        const formattedValue = value?.replace(/\D/g, "");

        if (
          formattedValue?.length > 0 &&
          formattedValue !== (country as CountryData)?.dialCode &&
          replaceDotPhoneFormat(
            (country as CountryData)?.format,
            formattedValue,
          ).includes("X") &&
          formattedValue?.length < (country as CountryData)?.dialCode.length + 9
        ) {
          return false;
        }

        return true;
      }),
  });

  return schema;
};

export const yupInputSelectOption = Yup.object().shape({
  id: ystr,
  value: ystr,
});

/**
 * Checks if a field in a Yup schema has a "required" test
 * @param schema - The Yup schema to analyze
 * @param fieldPath - The field path (supports nested paths like "accidentData.accidentDate" or "accidentData.witnessesList.0.name")
 * @returns true if the field has a required test, false otherwise
 */
export const isFieldRequired = (
  fieldPath: string,
  schema?: Yup.AnyObjectSchema,
): boolean => {
  if (!schema) return false;

  try {
    // Split the path to handle nested fields
    const pathParts = fieldPath.split(".");
    let currentSchema: any = schema;

    // Navigate through the schema following the path
    for (const part of pathParts) {
      // Skip numeric array indices (e.g. "0", "1", etc.)
      if (/^\d+$/.test(part)) {
        continue;
      }

      // If it's an object schema, access its fields
      if (currentSchema.fields && currentSchema.fields[part]) {
        currentSchema = currentSchema.fields[part];
      }
      // If the current schema doesn't have the field but has an innerType (it's an array)
      // it means we need to access the schema of the array elements
      else if (
        currentSchema.innerType &&
        currentSchema.innerType.fields &&
        currentSchema.innerType.fields[part]
      ) {
        currentSchema = currentSchema.innerType.fields[part];
      } else {
        // Field not found
        return false;
      }
    }

    // Check if the field has tests
    if (!currentSchema.tests || !Array.isArray(currentSchema.tests)) {
      return false;
    }

    // Look for a test with name "required"
    return currentSchema.tests.some(
      (test: any) =>
        test.OPTIONS?.name === "required" || test.name === "required",
    );
  } catch (error) {
    console.error(`Error checking field "${fieldPath}":`, error);
    return false;
  }
};

export const formatLabel = (
  validationSchema?: Yup.AnyObjectSchema,
  id = "",
  label = "",
) => {
  if (!label) return "";

  return isFieldRequired(id, validationSchema) ? `${label}* ` : `${label} `;
};

export const formatLabelWithCondition = (label = "", condition = false) => {
  if (!label) return "";

  return condition && !label.includes("*") ? `${label}* ` : `${label} `;
};

export const replaceDotPhoneFormat = (
  formatterPath: string,
  formattedNumber: string,
) => {
  let result = "";
  let dotIndex = 0;

  for (const char of formatterPath) {
    if (char === ".") {
      if (dotIndex < formattedNumber.length) {
        result += formattedNumber[dotIndex];
        dotIndex += 1;
      } else {
        result += "X";
      }
    } else {
      result += char;
    }
  }

  return result;
};
